/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetTriggerInstanceRequest,
  GetTriggerInstanceSecurity,
} from "../models/operations/gettriggerinstance.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildTriggerQuery,
  prefetchTrigger,
  queryKeyTrigger,
  TriggerQueryData,
} from "./trigger.core.js";
export {
  buildTriggerQuery,
  prefetchTrigger,
  queryKeyTrigger,
  type TriggerQueryData,
};

export type TriggerQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getTriggerInstance triggers
 *
 * @remarks
 * Get a trigger instance by ID.
 */
export function useTrigger(
  request: GetTriggerInstanceRequest,
  security?: GetTriggerInstanceSecurity | undefined,
  options?: QueryHookOptions<TriggerQueryData, TriggerQueryError>,
): UseQueryResult<TriggerQueryData, TriggerQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildTriggerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getTriggerInstance triggers
 *
 * @remarks
 * Get a trigger instance by ID.
 */
export function useTriggerSuspense(
  request: GetTriggerInstanceRequest,
  security?: GetTriggerInstanceSecurity | undefined,
  options?: SuspenseQueryHookOptions<TriggerQueryData, TriggerQueryError>,
): UseSuspenseQueryResult<TriggerQueryData, TriggerQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildTriggerQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setTriggerData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: TriggerQueryData,
): TriggerQueryData | undefined {
  const key = queryKeyTrigger(...queryKeyBase);

  return client.setQueryData<TriggerQueryData>(key, data);
}

export function invalidateTrigger(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "triggers", "get", ...queryKeyBase],
  });
}

export function invalidateAllTrigger(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "triggers", "get"],
  });
}
